/**
 * ADR-0263 §8 — defensive reader for the `sill_plan_intent` localStorage
 * key.
 *
 * The companion marketing PR adds `lib/plan-intent.ts` with the writer.
 * That PR stores JSON of the shape
 *
 *   { "plan": <string>, "interval": "monthly" | "yearly",
 *     "saved_at": <ISO string> }
 *
 * with a 7-day TTL. This reader lives in the dashboard and is
 * INTENTIONALLY narrow: it does not share code with the marketing
 * writer so the two PRs never depend on one landing before the other.
 * Any shape mismatch, parse error, missing field, or expired entry
 * reads as absent (the dashboard's post-sign-in flow silently skips
 * the agency-request POST on a bad value).
 */

export const PLAN_INTENT_STORAGE_KEY = 'sill_plan_intent';
export const PLAN_INTENT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export type PlanIntentInterval = 'monthly' | 'yearly';

export interface PlanIntent {
  readonly plan: string;
  readonly interval: PlanIntentInterval;
  readonly saved_at: string;
}

/** Parse the entry from localStorage. Returns null on any failure. */
export function readPlanIntent(
  storage: Pick<Storage, 'getItem'> | null,
  now: number = Date.now(),
): PlanIntent | null {
  if (storage === null) return null;
  let raw: string | null;
  try {
    raw = storage.getItem(PLAN_INTENT_STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null || raw.length === 0) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return null;
  }
  const bag = parsed as Record<string, unknown>;
  const plan = bag['plan'];
  const interval = bag['interval'];
  const saved_at = bag['saved_at'];
  if (typeof plan !== 'string' || plan.length === 0 || plan.length > 64) return null;
  if (interval !== 'monthly' && interval !== 'yearly') return null;
  if (typeof saved_at !== 'string' || saved_at.length === 0) return null;
  const parsed_at = Date.parse(saved_at);
  if (!Number.isFinite(parsed_at)) return null;
  if (now - parsed_at > PLAN_INTENT_TTL_MS) return null;
  // Guard against a clock going backwards: silently accept a future
  // saved_at (up to one TTL in the future) so a browser clock skew
  // doesn't silently drop every intent.
  if (parsed_at - now > PLAN_INTENT_TTL_MS) return null;
  return { plan, interval, saved_at };
}

/** Remove the entry regardless of parse state. */
export function clearPlanIntent(storage: Pick<Storage, 'removeItem'> | null): void {
  if (storage === null) return;
  try {
    storage.removeItem(PLAN_INTENT_STORAGE_KEY);
  } catch {
    // best effort; the next load will see the stale row and try again.
  }
}

/** True iff the plan slug is in the agency-plan namespace (`agency-*`). */
export function isAgencyPlanSlug(slug: string): boolean {
  return slug.startsWith('agency-');
}
